import { createEntityAdapter } from '@reduxjs/toolkit';
import { decodeVehicle } from 'helpers/timedSales';
import { isOlderVersion } from 'helpers/timedSales/versionChecker';
import { removeFromVehicleIds } from 'store/helpers/watchlist';
import { getFeaturedVehicleIds } from './featuredVehicles';

export const vehiclesAdapter = createEntityAdapter();

export const normalizeVehicleId = (vehicleId) => Number(vehicleId);
export const isValidVehicleId = (vehicleId) => {
	if (typeof vehicleId !== 'number' && (typeof vehicleId !== 'string' || !/^[1-9]\d*$/.test(vehicleId))) {
		return false;
	}
	const numericVehicleId = normalizeVehicleId(vehicleId);
	return Number.isSafeInteger(numericVehicleId) && numericVehicleId > 0;
};
export const normalizeVehicle = (vehicle) => ({ ...vehicle, id: normalizeVehicleId(vehicle.id) });
export const normalizeVehicleChanges = (changes) =>
	changes?.id == null ? changes : { ...changes, id: normalizeVehicleId(changes.id) };

const removeEntitiesThatAreNotPresentOnAnyList = (state) => {
	const vehicleIdsInLists = Object.values(state.searchLists)
		.reduce((accumulator, list) => {
			accumulator.push(...list.vehicleIds);
			accumulator.push(...getFeaturedVehicleIds(list?.featured));
			return accumulator;
		}, [])
		.map((id) => Number(id));
	const vehiclesToRemove = Object.keys(state.entities).filter(
		(vehicleId) => !vehicleIdsInLists.includes(Number(vehicleId)),
	);
	vehiclesAdapter.removeMany(state, vehiclesToRemove);
};

export const removeInactiveVdpEntity = (state, vehicleId) => {
	const isVehicleInList = Object.values(state.searchLists).some(
		(list) =>
			list.vehicleIds.some((id) => String(id) === String(vehicleId)) ||
			getFeaturedVehicleIds(list?.featured).some((id) => String(id) === String(vehicleId)),
	);
	if (!isVehicleInList) vehiclesAdapter.removeOne(state, vehicleId);
};

const discardOutDatedVehicles = ({ entities, vehicles }) => {
	return vehicles.filter(
		(vehicleToAdd) =>
			!entities[vehicleToAdd.id] ||
			!isOlderVersion({ incomingChanges: vehicleToAdd, storedState: entities[vehicleToAdd.id] }),
	);
};

export const updateVehicleIdsForAListKeyAndUpdateEntities = ({
	state,
	newVehicleList,
	listKey,
	isFeaturedVehicleList,
	campaignType,
	shouldDecode,
}) => {
	const updatedVehicles = discardOutDatedVehicles({ entities: state.entities, vehicles: newVehicleList });
	const newVehicles = (shouldDecode ? updatedVehicles.map(decodeVehicle) : updatedVehicles).map(normalizeVehicle);
	vehiclesAdapter.upsertMany(state, newVehicles);

	let searchList;
	if (isFeaturedVehicleList) {
		const featuredCampaigns = state.searchLists[listKey].featured?.featuredCampaigns || [];
		searchList = featuredCampaigns.find((campaign) => campaign.campaignType === campaignType);
	} else {
		searchList = state.searchLists[listKey];
	}

	searchList.vehicleIds = newVehicleList?.map(({ id }) => normalizeVehicleId(id)) || [];
	removeEntitiesThatAreNotPresentOnAnyList(state);
};

/**
 * It mutate state and remove vehicle from list
 */
export const removeVehicleFromListKeyAndUpdateEntities = ({ state, vehicleId, listKey }) => {
	const { vehicleIds, totalElements, pageCount } = removeFromVehicleIds({
		vehicleIds: state.searchLists[listKey].vehicleIds,
		vehicleIdToRemove: normalizeVehicleId(vehicleId),
		totalElements: state.searchLists[listKey].totalElements,
		itemsPerPage: state.searchLists[listKey].itemsPerPage,
	});

	// this will clear entities and update searchlist.vehicleIds
	const someVehicleWasRemovedFromList = vehicleIds.length !== state.searchLists[listKey].vehicleIds.length;
	if (someVehicleWasRemovedFromList) {
		const newVehicleList = vehicleIds.map((vehicleId) => state.entities[vehicleId]);
		updateVehicleIdsForAListKeyAndUpdateEntities({ state, newVehicleList, listKey, shouldDecode: false });
	}

	state.searchLists[listKey].totalElements = totalElements;
	state.searchLists[listKey].pageCount = pageCount;
	state.searchLists[listKey].totalElements;
};
